import { ColorSwatch, Flex, Stack, useMantineTheme } from '@mantine/core';
import classNames from 'classnames';
import React, { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CardV2 as Card,
  EmptyState,
  Heading,
  RadialBurstDiagram,
  Text,
  Tooltip,
  Well,
} from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import type { IAudienceBreakdownReturn } from './TargetAudienceOverview.useAudienceBreakdown';
import type { IAudienceStatsReturn } from './TargetAudienceOverview.useAudienceStats';
import {
  createNumberMap,
  getFormattedNumbers,
  getLookalikeUsers,
  getRawNumbers,
  getText,
} from './TargetAudienceOverview.utils';

import * as styles from './TargetAudienceOverview.css';

export type TReachabilityContext = 'wizard' | 'report';

interface ISharedProps {
  isSticky?: boolean;
  layout?: 'vertical' | 'horizontal';
  className?: string;
  isEmpty?: boolean;
  isLoading?: boolean;
  context?: TReachabilityContext;
  /** Flag: Whether or not to show Lookalike counts
   * This is a temporary flag until Lookalike targeting is fully rolled out
   * TODO: Remove this flag once Lookalike targeting is fully implemented
   */
  showLookalikeAudienceTargeting?: boolean;
}

export interface IStatsProps extends ISharedProps, IAudienceStatsReturn {
  context?: 'report';
}

interface IBreakdownProps extends ISharedProps, IAudienceBreakdownReturn {
  context?: 'wizard';
}

type TProps = IBreakdownProps | IStatsProps;

/**
 * TargetAudienceOverview component works with the useAudienceStats and useAudienceBreakdown hooks.
 * The stats hook displays stats on the report page and the breakdown hook displays the targeted
 * audience breakdown on the wizard page.
 *
 * const audienceData = useAudienceStats({ campaign, targetings, dailyStats });
 * const audienceData = useAudienceBreakdown({ campaign,targetings });
 *
 * Usage examples:
 * <TargetAudienceOverview {...audienceData} context="report" />
 * <TargetAudienceOverview {...audienceData} context="wizard" />
 * <TargetAudienceOverview {...audienceData} /> // defaults to wizard
 */
function TargetAudienceOverview(props: Readonly<IStatsProps>): JSX.Element;
function TargetAudienceOverview(props: Readonly<IBreakdownProps>): JSX.Element;
function TargetAudienceOverview(props: Readonly<TProps>): JSX.Element {
  const {
    isSticky = false,
    layout = 'vertical',
    className,
    isLoading,
    isEmpty,
    context = 'wizard',
    numTargetedReachableUsers,
    numTargetedReachableEmails,
    targetedLookalikeAudienceSize,
    numTargetedReachableCookies,
    numTargetedReachableDevices,
    numNewDevices,
    numDevicesPerPerson,
    showLookalikeAudienceTargeting,
  } = props;

  // Extract stats-specific properties only when we have stats props
  const isReport = context === 'report';

  const { t } = useTranslation();
  const theme = useMantineTheme();
  const textMap = getText(t, context);

  const targetedLookalikeAudienceSizeValue = getLookalikeUsers(
    numNewDevices,
    numTargetedReachableEmails,
    // Once we remove flag, it should just be `targetedLookalikeAudienceSize`
    showLookalikeAudienceTargeting
      ? targetedLookalikeAudienceSize || 'Moderate'
      : targetedLookalikeAudienceSize,
  );

  // Create number map with optional stats
  const numberMap = createNumberMap(
    numTargetedReachableUsers,
    numTargetedReachableEmails,
    targetedLookalikeAudienceSizeValue,
    numTargetedReachableCookies,
    numTargetedReachableDevices,
    numNewDevices,
    numDevicesPerPerson,
    isReport ? (props as IStatsProps) : undefined,
  );
  const formattedNumbers = getFormattedNumbers(numberMap, context);
  const rawNumbers = getRawNumbers(numberMap, context);

  return (
    <Card
      contentClassName={className}
      isSticky={isSticky}
      name={'target_audience_overview_card'}
      width={'full'}
    >
      <Card.Header title={textMap.cardTitle} />

      <Card.Content
        addVerticalGap={true}
        isLoading={isLoading}
        name={'reachability_breakdown_card'}
      >
        {isEmpty ? (
          <EmptyState label={textMap.emptyStateLabel} theme={'slate'} />
        ) : (
          <Flex direction={isReport ? 'row' : 'column'} gap={theme.spacing['4']}>
            <Stack flex={1} gap={theme.spacing['4']}>
              <Stack align={'center'} gap={theme.spacing['2']}>
                <Heading mantineComponent={'span'} name={'reachability_value'} variant={'h1'}>
                  {formattedNumbers.reachedUsers}
                </Heading>
                <Text.DefaultRegular>
                  {textMap.uniquePeopleLabel}{' '}
                  <Tooltip title={textMap.uniquePeopleTooltip}>{getIconForAction('info')}</Tooltip>
                </Text.DefaultRegular>

                {isReport ? (
                  <Text.SmallRegular>
                    {t('{{percentTargeted}} of targeted audience reached ({{targetedAudience}})', {
                      percentTargeted: formattedNumbers.reachedUsersPercent,
                      targetedAudience: formattedNumbers.targetedUsers,
                    })}
                  </Text.SmallRegular>
                ) : undefined}
              </Stack>

              <Well layout={'vertical'}>
                <Text.DefaultSemibold>{textMap.peopleReachedViaLabel}</Text.DefaultSemibold>

                <Flex justify={'space-between'}>
                  <Flex align={'flex-start'}>
                    <ColorSwatch color={theme.colors.teal[5]} mr={'2'} mt={'1'} size={'12'} />
                    <Text.DefaultMedium>
                      {textMap.emailLabel}{' '}
                      <Tooltip title={textMap.emailTooltip}>{getIconForAction('info')}</Tooltip>
                    </Text.DefaultMedium>
                  </Flex>

                  {isReport ? (
                    <Text.DefaultSemibold inline={true}>
                      {formattedNumbers.reachedEmails}
                      <Text.DefaultRegular inline={true}>
                        {' '}
                        / {formattedNumbers.targetedEmails}
                      </Text.DefaultRegular>
                    </Text.DefaultSemibold>
                  ) : (
                    <Text.DefaultSemibold>{formattedNumbers.targetedEmails}</Text.DefaultSemibold>
                  )}
                </Flex>

                <Flex justify={'space-between'}>
                  <Flex align={'flex-start'}>
                    <ColorSwatch color={theme.colors.slate[1]} mr={'2'} mt={'1'} size={'12'} />
                    <Text.DefaultMedium>
                      {textMap.cookieLabel}{' '}
                      <Tooltip title={textMap.cookieTooltip}>{getIconForAction('info')}</Tooltip>
                    </Text.DefaultMedium>
                  </Flex>

                  {isReport ? (
                    <Text.DefaultSemibold inline={true}>
                      {formattedNumbers.reachedCookies}
                      <Text.DefaultRegular inline={true}>
                        {' '}
                        / {formattedNumbers.targetedCookies}
                      </Text.DefaultRegular>
                    </Text.DefaultSemibold>
                  ) : (
                    <Text.DefaultSemibold>{formattedNumbers.targetedCookies}</Text.DefaultSemibold>
                  )}
                </Flex>

                {showLookalikeAudienceTargeting && (
                  <Flex justify={'space-between'}>
                    <Flex align={'flex-start'}>
                      <ColorSwatch color={theme.colors.yellow[5]} mr={'2'} mt={'1'} size={'12'} />
                      <Text.DefaultMedium>
                        {textMap.lookalikeLabel}{' '}
                        <Tooltip title={textMap.lookalikeTooltip}>
                          {getIconForAction('info')}
                        </Tooltip>
                      </Text.DefaultMedium>
                    </Flex>
                    <Text.DefaultSemibold>
                      {context === 'report' ? formattedNumbers.lookalikeUsers : 'Moderate'}
                    </Text.DefaultSemibold>
                  </Flex>
                )}
                <div className={styles.additionalText}>
                  <Text.SmallRegular>{textMap.additionalText}</Text.SmallRegular>
                </div>
              </Well>

              <Well layout={'vertical'}>
                <Text.DefaultSemibold>{textMap.devicesReachLabel}</Text.DefaultSemibold>
                <Flex justify={'space-between'} wrap={'wrap'}>
                  <Flex align={'flex-start'}>
                    <ColorSwatch color={theme.colors.slate[1]} mr={'2'} mt={'1'} size={'12'} />
                    <Text.DefaultMedium>
                      {textMap.devicesLabel}{' '}
                      <Tooltip title={textMap.devicesTooltip}>{getIconForAction('info')}</Tooltip>
                    </Text.DefaultMedium>
                  </Flex>
                  <Text.DefaultSemibold>{formattedNumbers.reachedDevices}</Text.DefaultSemibold>
                  <Text.DefaultRegular className={styles.secondaryText}>
                    {t('{{ num }} per person', { num: formattedNumbers.devicesPerPerson })}
                  </Text.DefaultRegular>
                </Flex>
                <Flex justify={'space-between'}>
                  <Flex align={'flex-start'} ml={isReport ? '4' : undefined}>
                    <ColorSwatch color={theme.colors.blue[5]} mr={'2'} mt={'1'} size={'12'} />
                    <Text.DefaultMedium>
                      {textMap.newDevicesLabel}{' '}
                      <Tooltip title={textMap.newDevicesTooltip}>
                        {getIconForAction('info')}
                      </Tooltip>
                    </Text.DefaultMedium>
                  </Flex>

                  {isReport ? (
                    <Text.DefaultRegular>{formattedNumbers.newDevices}</Text.DefaultRegular>
                  ) : (
                    <Text.DefaultSemibold>
                      {numberMap.newDevices.report > 0 && '~'}
                      {formattedNumbers.newDevices}
                    </Text.DefaultSemibold>
                  )}
                </Flex>
              </Well>
            </Stack>

            <Flex
              align={'center'}
              className={classNames(styles.chartWrapper, styles[layout])}
              flex={1}
              justify={'center'}
            >
              <Well className={styles.chartWell} width={'full'}>
                <Flex
                  align={'center'}
                  className={classNames(styles.chartWrapper, styles[layout])}
                  flex={1}
                  justify={'center'}
                >
                  <RadialBurstDiagram
                    countBlue={rawNumbers.chartBlueCount}
                    countTeal={rawNumbers.chartTealCount}
                    countYellow={rawNumbers.chartYellowCount}
                  />
                </Flex>
              </Well>
            </Flex>
          </Flex>
        )}
      </Card.Content>
    </Card>
  );
}

export default TargetAudienceOverview;
